export const LIVEKIT_TRACE_STATE_KEY = "vh";
export const LIVEKIT_TRACE_STATE_VALUE = "voice";

export const LIVEKIT_SESSION_MARKER_SPAN_NAME = "valerie.api.request";
export const LIVEKIT_ENDPOINT_ROOT_SPAN_NAME = "valerie.api.endpoint";
export const LIVEKIT_API_OPERATION_ATTRIBUTE = "valerie.api.operation";

export const LIVEKIT_ENDPOINT_TRACE_ID_ATTRIBUTE = "valerie.endpoint.trace_id";
export const LIVEKIT_ENDPOINT_SPAN_ID_ATTRIBUTE = "valerie.endpoint.span_id";
export const LIVEKIT_SESSION_TRACE_ID_ATTRIBUTE = "valerie.session.trace_id";
export const LIVEKIT_SESSION_SPAN_ID_ATTRIBUTE = "valerie.session.span_id";

export const LIVEKIT_TRACED_HTTP_ROUTES = [
  "/api/agent/appointments/availability",
  "/api/agent/appointments/book",
  "/api/agent/appointments/booked",
  "/api/agent/appointments/cancel",
  "/api/agent/appointments/reschedule",
  "/api/agent/appointments/scheduling-options",
  "/api/agent/appointments/reschedule-unit",
  "/api/agent/eligibility/check",
  "/api/agent/faq",
  "/api/agent/ingest-transcript",
  "/api/agent/locations",
  "/api/agent/office-directory",
  "/api/agent/scheduling/options",
  "/api/agent/scheduling/book",
  "/api/agent/scheduling/reschedule",
  "/api/agent/scheduling/eligibility",
  "/api/agent/scheduling/scope",
  "/api/agent/scheduling/cancel",
  "/api/agent/scheduling/booked",
  "/api/agent/scheduling/locations",
  "/api/agent/message-recovery-failures",
  "/api/agent/patient-cases",
  "/api/agent/patients/candidates-by-phone",
  "/api/agent/patients/create-from-intake",
  "/api/agent/patients/medications",
  "/api/agent/patients/names-by-phone",
  "/api/agent/patients/search-by-phone",
  "/api/agent/providers",
  "/api/agent/scheduling-rules/relevant",
  "/api/agent/telephone-encounters",
  "/api/agent/voice-csat",
] as const;

export type LivekitTracedHttpRoute = (typeof LIVEKIT_TRACED_HTTP_ROUTES)[number];

export enum LivekitApiOperation {
  BookAppointment = "book_appointment",
  CancelAppointment = "cancel_appointment",
  CheckEligibility = "check_eligibility",
  CommonBookAppointment = "common_book_appointment",
  CommonCancelAppointment = "common_cancel_appointment",
  CommonCheckEligibility = "common_check_eligibility",
  CommonGetBookedAppointments = "common_get_booked_appointments",
  CommonGetLocations = "common_get_locations",
  CommonGetRelevantSchedulingRules = "common_get_relevant_scheduling_rules",
  CommonGetSchedulingOptions = "common_get_scheduling_options",
  CommonRescheduleAppointment = "common_reschedule_appointment",
  CreatePatientCase = "create_patient_case",
  CreatePatientFromIntake = "create_patient_from_intake",
  CreateTelephoneEncounter = "create_telephone_encounter",
  GetAppointmentAvailability = "get_appointment_availability",
  GetBookedAppointments = "get_booked_appointments",
  GetClinicFaq = "get_clinic_faq",
  GetLocations = "get_locations",
  GetOfficeDirectory = "get_office_directory",
  GetPatientCandidatesByPhone = "get_patient_candidates_by_phone",
  GetPatientMedications = "get_patient_medications",
  GetPatientNamesByPhone = "get_patient_names_by_phone",
  GetProviders = "get_providers",
  GetRelevantSchedulingRules = "get_relevant_scheduling_rules",
  GetSchedulingOptions = "get_scheduling_options",
  IngestTranscript = "ingest_transcript",
  ReportMessageRecoveryFailure = "report_message_recovery_failure",
  RescheduleAppointment = "reschedule_appointment",
  RescheduleAppointmentUnit = "reschedule_appointment_unit",
  SearchPatientsByPhone = "search_patients_by_phone",
  SubmitVoiceCsat = "submit_voice_csat",
}

export const LIVEKIT_API_OPERATIONS = {
  "/api/agent/appointments/availability": LivekitApiOperation.GetAppointmentAvailability,
  "/api/agent/appointments/book": LivekitApiOperation.BookAppointment,
  "/api/agent/appointments/booked": LivekitApiOperation.GetBookedAppointments,
  "/api/agent/appointments/cancel": LivekitApiOperation.CancelAppointment,
  "/api/agent/appointments/reschedule": LivekitApiOperation.RescheduleAppointment,
  "/api/agent/appointments/scheduling-options": LivekitApiOperation.GetSchedulingOptions,
  "/api/agent/appointments/reschedule-unit": LivekitApiOperation.RescheduleAppointmentUnit,
  "/api/agent/eligibility/check": LivekitApiOperation.CheckEligibility,
  "/api/agent/faq": LivekitApiOperation.GetClinicFaq,
  "/api/agent/ingest-transcript": LivekitApiOperation.IngestTranscript,
  "/api/agent/locations": LivekitApiOperation.GetLocations,
  "/api/agent/office-directory": LivekitApiOperation.GetOfficeDirectory,
  "/api/agent/scheduling/options": LivekitApiOperation.CommonGetSchedulingOptions,
  "/api/agent/scheduling/book": LivekitApiOperation.CommonBookAppointment,
  "/api/agent/scheduling/reschedule": LivekitApiOperation.CommonRescheduleAppointment,
  "/api/agent/scheduling/eligibility": LivekitApiOperation.CommonCheckEligibility,
  "/api/agent/scheduling/scope": LivekitApiOperation.CommonGetRelevantSchedulingRules,
  "/api/agent/scheduling/cancel": LivekitApiOperation.CommonCancelAppointment,
  "/api/agent/scheduling/booked": LivekitApiOperation.CommonGetBookedAppointments,
  "/api/agent/scheduling/locations": LivekitApiOperation.CommonGetLocations,
  "/api/agent/message-recovery-failures": LivekitApiOperation.ReportMessageRecoveryFailure,
  "/api/agent/patient-cases": LivekitApiOperation.CreatePatientCase,
  "/api/agent/patients/candidates-by-phone": LivekitApiOperation.GetPatientCandidatesByPhone,
  "/api/agent/patients/create-from-intake": LivekitApiOperation.CreatePatientFromIntake,
  "/api/agent/patients/medications": LivekitApiOperation.GetPatientMedications,
  "/api/agent/patients/names-by-phone": LivekitApiOperation.GetPatientNamesByPhone,
  "/api/agent/patients/search-by-phone": LivekitApiOperation.SearchPatientsByPhone,
  "/api/agent/providers": LivekitApiOperation.GetProviders,
  "/api/agent/scheduling-rules/relevant": LivekitApiOperation.GetRelevantSchedulingRules,
  "/api/agent/telephone-encounters": LivekitApiOperation.CreateTelephoneEncounter,
  "/api/agent/voice-csat": LivekitApiOperation.SubmitVoiceCsat,
} as const satisfies Record<LivekitTracedHttpRoute, LivekitApiOperation>;

type ExtractVoiceApiPath<Route> = Route extends `/api/agent${infer Path}` ? Path : never;
export type LivekitVoiceApiPath = ExtractVoiceApiPath<LivekitTracedHttpRoute>;

const livekitTracedHttpRouteSet = new Set<string>(LIVEKIT_TRACED_HTTP_ROUTES);

export function isLivekitTracedHttpRoute(route: string): boolean {
  return livekitTracedHttpRouteSet.has(route);
}

export function getLivekitApiOperation(route: string): LivekitApiOperation | undefined {
  return isLivekitTracedHttpRoute(route) ? LIVEKIT_API_OPERATIONS[route as LivekitTracedHttpRoute] : undefined;
}
